/**
 * App-wide time system. Every timestamp the app *displays* is rendered in
 * Pacific Time (America/Los_Angeles), regardless of the viewer's local timezone.
 * Timestamps round-trip from the API as UTC instants (Postgres TIMESTAMPTZ); we
 * only re-format on the way out.
 *
 * Use these helpers everywhere instead of bare `new Date(x).toLocaleString()`
 * etc. on TIMESTAMPTZ values. (For DATE-only columns like task due dates, keep
 * using `formatTaskDate` — those are calendar dates, not instants, and must not
 * be timezone-shifted.)
 */

export const APP_TIME_ZONE = 'America/Los_Angeles';

/** "PT" suffix — convenient for labels next to a formatted time. */
export const APP_TIME_ZONE_LABEL = 'PT';

function toDate(value: Date | string | number | null | undefined): Date | null {
  if (value == null) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const s = typeof value === 'string' ? value.trim() : value;
  if (s === '' || s === null) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "5/18/2026, 12:34:56 PM" in Pacific Time. */
export function formatDateTimePT(
  value: Date | string | number | null | undefined,
  options: Intl.DateTimeFormatOptions = {},
  fallback = '—'
): string {
  const d = toDate(value);
  if (!d) return fallback;
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZone: APP_TIME_ZONE,
    ...options,
  });
}

/** "5/18/2026" in Pacific Time. */
export function formatDatePT(
  value: Date | string | number | null | undefined,
  options: Intl.DateTimeFormatOptions = {},
  fallback = '—'
): string {
  const d = toDate(value);
  if (!d) return fallback;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    timeZone: APP_TIME_ZONE,
    ...options,
  });
}

/** "12:34 PM" in Pacific Time. */
export function formatTimePT(
  value: Date | string | number | null | undefined,
  options: Intl.DateTimeFormatOptions = {},
  fallback = '—'
): string {
  const d = toDate(value);
  if (!d) return fallback;
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: APP_TIME_ZONE,
    ...options,
  });
}

/** Long form: "May 18, 2026, 12:34 PM PT". */
export function formatDateTimeLongPT(
  value: Date | string | number | null | undefined,
  fallback = '—'
): string {
  const formatted = formatDateTimePT(
    value,
    {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: undefined,
    },
    fallback
  );
  return formatted === fallback ? fallback : `${formatted} ${APP_TIME_ZONE_LABEL}`;
}

/**
 * Year-Month-Day Pacific calendar parts for a given instant. Use when grouping
 * by Pacific calendar day (e.g. "what month is this note in PT?").
 */
export function pacificYMD(value: Date | string | number): { year: number; month: number; day: number } | null {
  const d = toDate(value);
  if (!d) return null;
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: APP_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const get = (t: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === t)?.value ?? NaN);
  const year = get('year');
  const month = get('month');
  const day = get('day');
  if ([year, month, day].some((n) => Number.isNaN(n))) return null;
  return { year, month, day };
}
